import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import ParkProLogoWhiteLong from "../../../assets/ParkPro White_Long.svg";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";
import { getLandingPathForRole } from "../../../utils/roleUtils";

// Custom styles for the dark theme login page
const darkThemeStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
  
  @keyframes glow {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.4; }
  }
  
  .animation-delay-2000 {
    animation-delay: 2s;
  }
  
  .animation-delay-4000 {
    animation-delay: 4s;
  }
  
  .animate-float {
    animation: float 6s ease-in-out infinite;
  }
  
  .animate-glow {
    animation: glow 4s ease-in-out infinite;
  }
  
  /* Disable underline/hover animation specifically for the CTA link */
  .no-underline-anim,
  .no-underline-anim:hover,
  .no-underline-anim:focus {
    text-decoration: none !important;
    background-image: none !important;
    box-shadow: none !important;
    transition: none !important;
  }
  .no-underline-anim::after,
  .no-underline-anim:hover::after,
  .no-underline-anim:focus::after {
    content: none !important;
    display: none !important;
  }
`;

export default function ModernLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const navigate = useNavigate();

  // Note: Auth check is handled by LoginRouteGuard component which wraps this route
  // No need for duplicate check here

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(""); // Clear any previous errors

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        // Login endpoint only returns { success: true }, need to fetch user to get role
        // Fetch user data to determine role for redirect
        try {
          const userRes = await fetch(`${import.meta.env.VITE_API_URL}/api/me`, {
            credentials: "include",
          });
          
          if (userRes.ok) {
            const userData = await userRes.json();
            toast.success(`Welcome back, ${userData?.firstname || "Agent"}! ✨`);
            // Role-based redirect after successful login
            const landingPath = getLandingPathForRole(userData?.role);
            navigate(landingPath, { replace: true });
          } else {
            // If we can't get user data, default to agent dashboard
            toast.success("Welcome back! ✨");
            navigate("/agent/dashboard", { replace: true });
          }
        } catch (userErr) {
          // If fetching user fails, default to agent dashboard
          console.warn("Failed to fetch user after login:", userErr);
          toast.success("Welcome back! ✨");
          navigate("/agent/dashboard", { replace: true });
        }
      } else {
        const errorData = await res.json();
        
        // Handle rate limit (429) with special messaging
        if (res.status === 429) {
          const rateLimitData = errorData;
          setIsRateLimited(true);
          setRateLimitInfo({
            attemptsRemaining: 0,
            attemptsUsed: rateLimitData.attemptsUsed || 5,
            maxAttempts: rateLimitData.maxAttempts || 5,
            minutesUntilReset: rateLimitData.minutesUntilReset || 15,
            resetTime: rateLimitData.resetTime
          });
          
          const errorMessage = rateLimitData.message || 
            `Too many failed login attempts. Please try again in ${rateLimitData.minutesUntilReset || 15} minute${rateLimitData.minutesUntilReset !== 1 ? 's' : ''}.`;
          setError(errorMessage);
          toast.error(errorMessage);
        } else {
          // Handle regular login errors (401) with rate limit info
          setIsRateLimited(false);
          
          // Extract rate limit info from error data (only for failed attempts)
          if (errorData.rateLimitInfo) {
            setRateLimitInfo(errorData.rateLimitInfo);
          } else {
            setRateLimitInfo(null);
          }
          
          const errorMessage = errorData.message || "Invalid username or password. Please try again.";
          setError(errorMessage);
          toast.error(errorMessage);
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorMessage = "Network error. Please check your connection and try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Clear error when user starts typing
  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    if (error) {
      setError("");
      setRateLimitInfo(null);
      setIsRateLimited(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) {
      setError("");
      setRateLimitInfo(null);
      setIsRateLimited(false);
    }
  };

  return (
    <>
      <style>{darkThemeStyles}</style>
      <div className="flex overflow-hidden relative min-h-screen bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
        {/* Main Content */}
        <div className="flex relative z-10 flex-1 justify-center items-center px-6 py-12">
          <div className="w-full max-w-md">
            {/* Logo and Header */}
            <div className="mb-8 text-center">
              <div className="flex justify-center mb-6">
                <img
                  src={ParkProLogoWhiteLong}
                  alt="ParkPro Logo"
                  className="w-auto h-16 transition-transform duration-300 hover:scale-105"
                  style={{ objectFit: "contain" }}
                />
              </div>
              <h1 className="mb-2 text-3xl font-bold text-white">
                Welcome Back!
              </h1>
              <p className="text-gray-300">
                Sign in to your ParkPro Agent Account
              </p>
            </div>

            {/* Login Form */}
            <div className="p-8 bg-white rounded-2xl border border-gray-200 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Username Field */}
                <div>
                  <label
                    htmlFor="username"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    required
                    className={`px-4 py-3 w-full h-12 text-base placeholder-gray-400 text-gray-900 bg-gray-50 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      error ? "border-red-300 focus:ring-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your username"
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      required
                      className={`px-4 py-3 pr-12 w-full h-12 text-base placeholder-gray-400 text-gray-900 bg-gray-50 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        error ? "border-red-300 focus:ring-red-500" : "border-gray-300"
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="flex absolute top-0 right-0 bottom-0 items-center p-0 px-3 m-0 h-full text-gray-400 bg-transparent rounded-none border-none shadow-none transition-colors hover:text-gray-600 focus:outline-none"
                      tabIndex={0}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className={`flex flex-col p-4 rounded-xl border ${
                    isRateLimited 
                      ? 'bg-orange-50 border-orange-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start space-x-3">
                      <AlertCircle className={`flex-shrink-0 mt-0.5 w-5 h-5 ${
                        isRateLimited ? 'text-orange-600' : 'text-red-600'
                      }`} />
                      <div className="flex-1">
                        <p className={`text-sm ${
                          isRateLimited ? 'text-orange-800' : 'text-red-800'
                        }`}>
                          {error}
                        </p>
                        {isRateLimited && rateLimitInfo?.minutesUntilReset && (
                          <p className="mt-2 text-xs text-orange-700">
                            Try again in {rateLimitInfo.minutesUntilReset} minute{rateLimitInfo.minutesUntilReset !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Remember Me & Forgot Password */}
                <div className="flex justify-between items-center">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-50 rounded border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Remember me
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-blue-600 transition-colors hover:text-blue-700"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <div className="mr-2 w-5 h-5 rounded-full border-b-2 border-white animate-spin"></div>
                      Signing in...
                    </div>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="flex absolute inset-0 items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="flex relative justify-center text-sm">
                  <span className="px-3 text-gray-600 bg-white">
                    New to ParkPro?
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-xs text-gray-500">
                By signing in, you agree to our{" "}
                <Link
                  to="/terms"
                  className="text-blue-600 transition-colors hover:text-blue-700"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy"
                  className="text-blue-600 transition-colors hover:text-blue-700"
                >
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}